import type { ArrowData, Cell, Direction, Level, ShapeName } from './types';

// Frozen v1 implementation: existing unversioned prototype attempts must
// regenerate the same cells, arrow IDs, seed, and timer on resume or retry.
// New content is generated by generator.ts.

export const GENERATOR_VERSION = 1;
const DIRS: Record<Direction, Cell> = { UP: { x: 0, y: -1 }, DOWN: { x: 0, y: 1 }, LEFT: { x: -1, y: 0 }, RIGHT: { x: 1, y: 0 } };
const DIRECTION_NAMES: Direction[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];
const key = (p: Cell) => `${p.x},${p.y}`;
const add = (a: Cell, b: Cell): Cell => ({ x: a.x + b.x, y: a.y + b.y });
const equals = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;

export function randomSource(seed: number): () => number {
  let x = seed >>> 0;
  return () => {
    x += 0x6D2B79F5;
    let t = x;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffle<T>(items: T[], rand: () => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
export const SHAPES: ShapeName[] = ['rectangle', 'square', 'circle', 'diamond', 'cross', 'star', 'l-shape', 'custom', 'hollow'];

export function activeCells(width: number, height: number, shape: ShapeName): Cell[] {
  const out: Cell[] = [];
  const cx = (width - 1) / 2, cy = (height - 1) / 2;
  const rx = Math.max(1, cx), ry = Math.max(1, cy);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const nx = (x - cx) / rx, ny = (y - cy) / ry;
    let inside = true;
    switch (shape) {
      case 'circle': inside = nx * nx + ny * ny <= 1.09; break;
      case 'diamond': inside = Math.abs(nx) + Math.abs(ny) <= 1.12; break;
      case 'cross': inside = Math.abs(nx) <= 0.35 || Math.abs(ny) <= 0.35; break;
      case 'star': {
        const angle = Math.atan2(ny, nx) + Math.PI / 2;
        const theta = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const sector = theta / (Math.PI / 5);
        const fraction = sector - Math.floor(sector);
        const outer = Math.floor(sector) % 2 === 0;
        const limit = outer ? 1 - 0.45 * fraction : 0.55 + 0.45 * fraction;
        inside = Math.hypot(nx, ny) <= limit * 1.12;
        break;
      }
      case 'l-shape': inside = x <= Math.floor(width * 0.36) || y >= Math.floor(height * 0.59); break;
      case 'custom': inside = (x <= Math.floor(width * 0.7) && y <= Math.floor(height * 0.55)) ||
        (x >= Math.floor(width * 0.32) && y >= Math.floor(height * 0.35)); break;
      case 'hollow': inside = !(Math.abs(nx) < 0.38 && Math.abs(ny) < 0.38); break;
      case 'rectangle':
      case 'square': break;
    }
    if (inside) out.push({ x, y });
  }
  return out;
}

export function canSlide(arrow: ArrowData, active: Set<string>, occupied: Set<string>): boolean {
  const direction = DIRS[arrow.direction];
  const tip = arrow.cells[arrow.cells.length - 1];
  let pos = add(tip!, direction);
  while (active.has(key(pos))) {
    if (occupied.has(key(pos))) return false;
    pos = add(pos, direction);
  }
  return true;
}

export function isSolvable(arrows: ArrowData[], active: Cell[]): boolean {
  const activeKeys = new Set(active.map(key));
  const remaining = new Map(arrows.map(a => [a.id, a]));
  while (remaining.size) {
    const occupied = new Set([...remaining.values()].flatMap(a => a.cells.map(key)));
    const removable = [...remaining.values()].find(a => canSlide(a, activeKeys, occupied));
    if (!removable) return false;
    remaining.delete(removable.id);
  }
  return true;
}

export function validateLevel(level: Level): string[] {
  const issues: string[] = [];
  const active = new Set(level.active.map(key));
  const taken = new Set<string>();
  for (const a of level.arrows) {
    if (a.cells.length < 2) issues.push(`${a.id}: arrow too short`);
    const visited = new Set<string>();
    for (let i = 0; i < a.cells.length; i++) {
      const cell = a.cells[i];
      if (!active.has(key(cell!))) issues.push(`${a.id}: out of shape ${key(cell!)}`);
      if (taken.has(key(cell!))) issues.push(`${a.id}: occupied twice ${key(cell!)}`);
      if (visited.has(key(cell!))) issues.push(`${a.id}: self overlap ${key(cell!)}`);
      if (i > 0 && Math.abs(cell!.x - a.cells[i - 1]!.x) + Math.abs(cell!.y - a.cells[i - 1]!.y) !== 1) issues.push(`${a.id}: nonadjacent cells`);
      visited.add(key(cell!));
      taken.add(key(cell!));
    }
    if (a.cells.length >= 2) {
      const tip = a.cells[a.cells.length - 1], prior = a.cells[a.cells.length - 2];
      if (!equals({ x: tip!.x - prior!.x, y: tip!.y - prior!.y }, DIRS[a.direction])) issues.push(`${a.id}: kinked head`);
      let p = add(tip!, DIRS[a.direction]);
      while (active.has(key(p))) {
        if (visited.has(key(p))) issues.push(`${a.id}: self blocked exit`);
        p = add(p, DIRS[a.direction]);
      }
    }
  }
  if (!level.arrows.length) issues.push('no arrows');
  if (!isSolvable(level.arrows, level.active)) issues.push('level has deadlock');
  return issues;
}

function generateCandidate(
  active: Set<string>, occupied: Set<string>, available: Cell[],
  rand: () => number, desiredLength: number
): Omit<ArrowData, 'id'> | null {
  const shuffledTips = shuffle(available, rand);
  for (const tip of shuffledTips.slice(0, 65)) {
    for (const direction of shuffle(DIRECTION_NAMES, rand)) {
      const forward = DIRS[direction];
      const firstBack = { x: tip.x - forward.x, y: tip.y - forward.y };
      if (!active.has(key(firstBack)) || occupied.has(key(firstBack))) continue;
      let exit = add(tip, forward), clear = true;
      const exitCells = new Set<string>();
      while (active.has(key(exit))) {
        exitCells.add(key(exit));
        if (occupied.has(key(exit))) { clear = false; break; }
        exit = add(exit, forward);
      }
      if (!clear) continue;
      const backwards: Cell[] = [tip, firstBack];
      const used = new Set([key(tip), key(firstBack)]);
      const target = Math.max(2, desiredLength);
      for (let i = 2; i < target; i++) {
        const last = backwards[backwards.length - 1];
        const options = shuffle(DIRECTION_NAMES, rand)
          .map(d => add(last!, DIRS[d]))
          .filter(c => active.has(key(c)) && !occupied.has(key(c)) && !used.has(key(c)) && !exitCells.has(key(c)));
        if (!options.length) break;
        const next = options[0];
        backwards.push(next!);
        used.add(key(next!));
      }
      if (backwards.length >= Math.min(target, 3)) return { cells: backwards.reverse(), direction };
    }
  }
  return null;
}

function makeShape(levelNumber: number): ShapeName {
  if (levelNumber <= 2) return 'square';
  if (levelNumber <= 5) return 'rectangle';
  return SHAPES[(levelNumber - 1) % SHAPES.length]!;
}
export function seedForLevel(level: number): number {
  return (Math.imul(level, 2654435761) ^ 0xA11CE123) >>> 0;
}

export function generateLevel(number: number, seed = seedForLevel(number), overrideShape?: ShapeName): Level {
  const tier = Math.min(4, Math.floor((number - 1) / 20));
  const shape = overrideShape ?? makeShape(number);
  const width = Math.max(['cross', 'star', 'hollow'].includes(shape) ? 7 : 5, Math.min(12, 5 + Math.floor((number - 1) / 15)));
  const height = shape === 'rectangle' ? Math.min(12, width + 1) : width;
  const active = activeCells(width, height, shape);
  const activeSet = new Set(active.map(key));
  const desiredCount = Math.min(13, 4 + tier * 2 + Math.floor((number % 10) / 6));
  let best: ArrowData[] = [];
  let bestScore = -1;
  for (let round = 0; round < 10; round++) {
    const rand = randomSource((seed + round * 0x9E3779B9) >>> 0);
    const occupied = new Set<string>();
    const arrows: ArrowData[] = [];
    for (let index = 0; index < desiredCount; index++) {
      const available = active.filter(p => !occupied.has(key(p)));
      const length = Math.min(8, 3 + tier + Math.floor(rand() * 3));
      const candidate = generateCandidate(activeSet, occupied, available, rand, length);
      if (!candidate) break;
      const arrow: ArrowData = { ...candidate, id: `a${index + 1}` };
      arrows.push(arrow);
      for (const c of arrow.cells) occupied.add(key(c));
    }
    const blocked = arrows.filter(a => !canSlide(a, activeSet, occupied)).length;
    const score = arrows.length * 4 + Math.min(blocked, 4) * (number > 3 ? 2 : 0);
    if (score > bestScore) { bestScore = score; best = arrows; }
    if (arrows.length >= desiredCount && (blocked >= 1 || number <= 3)) break;
  }
  const timeLimit = 70 + tier * 22 + Math.max(0, best.length - 4) * 7;
  const level: Level = {
    id: `level-${number}`, number, seed, generatorVersion: GENERATOR_VERSION,
    shape, width, height, active, arrows: best, difficulty: tier + 1, timeLimit
  };
  const issues = validateLevel(level);
  if (issues.length) throw new Error(`Generator failed ${number}/${shape}: ${issues.join(', ')}`);
  return level;
}

export function generateDaily(date: string): Level {
  const seed = [...date].reduce((sum, char) => Math.imul(sum ^ char.charCodeAt(0), 16777619) >>> 0, 2166136261);
  const number = 38 + seed % 35;
  const base = generateLevel(number, seed);
  return { ...base, id: `daily-${date}`, number: 0, timeLimit: 115 + base.difficulty * 12 };
}
